import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "react-bulma-components";
import { AdminStats, AdminUserData, DiscreteOptionsMigrationResult, MinAvgMax, fetchAdminStats, adminLookupUser, adminSetEntitlement, runDiscreteOptionsMigration } from "../../models/admin";
import { useFlags } from "../flags/FeatureFlagProvider";
import { FeatureGate } from "../flags/FeatureGate";
import { ALL_FEATURE_FLAGS, FeatureFlagName } from "../flags/featureFlags";

function StatRow({ label, value, warn }: { label: string; value: string | number; warn?: boolean }) {
  return (
    <tr>
      <td>{label}</td>
      <td className={warn ? "has-text-warning" : ""}><strong>{value}</strong></td>
    </tr>
  )
}

function MinAvgMaxTable({ rows }: { rows: { label: string; data: MinAvgMax }[] }) {
  return (
    <table className="table is-fullwidth is-narrow">
      <thead>
        <tr><th></th><th>Min</th><th>Avg</th><th>Max</th></tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            <td>{row.label}</td>
            <td>{row.data?.min ?? "—"}</td>
            <td>{row.data?.avg ?? "—"}</td>
            <td>{row.data?.max ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function StatsTable({ stats }: { stats: AdminStats }) {
  const { t } = useTranslation();
  return (
    <>
      <table className="table is-fullwidth is-narrow">
        <tbody>
          <StatRow label={t("admin.stats.totalUsers")} value={stats.totalUsers} />
          <StatRow label={t("admin.stats.activeUsers")} value={stats.activeUsers} />
          <StatRow label={t("admin.stats.totalBoards")} value={stats.totalBoards} />
          <StatRow label={t("admin.stats.orphanedBoards")} value={stats.orphanedBoards} warn={stats.orphanedBoards > 0} />
          <StatRow label={t("admin.stats.memberlessBoards")} value={stats.memberlessBoards} warn={stats.memberlessBoards > 0} />
          <StatRow label={t("admin.stats.boardsWithNotes")} value={stats.boardsWithNotes} />
          <StatRow label={t("admin.stats.totalNotes")} value={stats.totalNotes} />
          <StatRow label={t("admin.stats.newSessionsThisWeek")} value={stats.newSessionsThisWeek} />
          <StatRow label={t("admin.stats.usersByTier")} value={`${stats.usersByTier.free} / ${stats.usersByTier.supporter} / ${stats.usersByTier.pro}`} />
          <StatRow label={t("admin.stats.newUsersThisWeek")} value={stats.newUsersThisWeek} />
        </tbody>
      </table>
      <MinAvgMaxTable rows={[
        { label: t("admin.stats.boardsPerUser"), data: stats.boardsPerUser },
        { label: t("admin.stats.sessionsPerBoard"), data: stats.sessionsPerBoard },
        { label: t("admin.stats.itemsPerSession"), data: stats.itemsPerSession },
        { label: t("admin.stats.notesPerBoard"), data: stats.notesPerBoard },
      ]} />
    </>
  )
}

function DiscreteOptionsMigrationPanel() {
  const [dryRunLoading, setDryRunLoading] = useState(false)
  const [migrateLoading, setMigrateLoading] = useState(false)
  const [dryRunResult, setDryRunResult] = useState<DiscreteOptionsMigrationResult | null>(null)
  const [migrateResult, setMigrateResult] = useState<DiscreteOptionsMigrationResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleDryRun = async () => {
    setDryRunLoading(true)
    setError(null)
    setDryRunResult(null)
    try {
      const result = await runDiscreteOptionsMigration(true)
      setDryRunResult(result)
    } catch (e: any) {
      setError(e.message || "Dry run failed")
    } finally {
      setDryRunLoading(false)
    }
  }

  const handleMigrate = async () => {
    setMigrateLoading(true)
    setError(null)
    setMigrateResult(null)
    try {
      const result = await runDiscreteOptionsMigration(false)
      setMigrateResult(result)
      setDryRunResult(null)
    } catch (e: any) {
      setError(e.message || "Migration failed")
    } finally {
      setMigrateLoading(false)
    }
  }

  const formatResult = (result: DiscreteOptionsMigrationResult) =>
    `${result.dryRun ? "[DRY RUN] " : ""}Items: ${result.itemsMigrated}, Polls: ${result.pollsMigrated}`

  return (
    <>
      <hr />
      <p className="subtitle">Migrate Poll Options to UIDs</p>
      {error && <p className="has-text-danger">{error}</p>}
      {dryRunResult && <p className="has-text-info">{formatResult(dryRunResult)}</p>}
      {migrateResult && <p className="has-text-success">{formatResult(migrateResult)}</p>}
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <Button size="small" color="info" outlined loading={dryRunLoading} onClick={handleDryRun}>
          Dry Run
        </Button>
        <Button
          size="small"
          color="warning"
          outlined
          loading={migrateLoading}
          onClick={handleMigrate}
          disabled={!dryRunResult}
        >
          Migrate
        </Button>
      </div>
    </>
  )
}

function EntitlementToggle({ user, tier, onUpdate }: {
  user: AdminUserData
  tier: "supporter" | "pro"
  onUpdate: () => void
}) {
  const entitlement = user.entitlements?.[tier] as { active?: boolean; expiresAt?: number | null } | undefined;
  const isActive = entitlement?.active === true;
  const [saving, setSaving] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string>(
    entitlement?.expiresAt ? new Date(entitlement.expiresAt).toISOString().slice(0, 10) : ""
  );

  const handleToggle = async () => {
    setSaving(true);
    try {
      await adminSetEntitlement({
        targetUid: user.uid,
        tier,
        active: !isActive,
        ...(tier === "pro" ? { expiresAt: expiresAt ? new Date(expiresAt).getTime() : null } : {}),
      });
      onUpdate();
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr>
      <td>{tier}</td>
      <td style={{ textAlign: "center" }}>
        <span className={`icon is-clickable ${saving ? "has-text-grey-light" : ""}`} onClick={saving ? undefined : handleToggle}>
          <i className={isActive ? "icon-check is-large" : "icon-check-empty is-large"} aria-hidden="true"></i>
        </span>
      </td>
      <td>
        {tier === "pro" && (
          <input
            type="date"
            className="input is-small"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            placeholder="No expiry"
            style={{ maxWidth: "10rem" }}
          />
        )}
      </td>
    </tr>
  );
}

function UserEntitlementPanel() {
  const [query, setQuery] = useState("");
  const [user, setUser] = useState<AdminUserData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLookup = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setUser(null);
    try {
      const isEmail = query.includes("@");
      const result = await adminLookupUser(isEmail ? { email: query.trim() } : { uid: query.trim() });
      setUser(result);
    } catch (e: any) {
      setError(e.message || "User not found");
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    if (!user) return;
    try {
      const result = await adminLookupUser({ uid: user.uid });
      setUser(result);
    } catch { /* keep stale data on refresh failure */ }
  };

  return (
    <>
      <hr />
      <p className="subtitle">User Entitlements</p>
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}>
        <input
          className="input is-small"
          type="text"
          placeholder="UID or email"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleLookup()}
          style={{ flex: 1 }}
        />
        <Button size="small" color="info" outlined loading={loading} onClick={handleLookup}>
          Lookup
        </Button>
      </div>
      {error && <p className="has-text-danger is-size-7">{error}</p>}
      {user && (
        <>
          <table className="table is-fullwidth is-narrow">
            <tbody>
              <tr><td>UID</td><td><strong style={{ fontSize: "0.75rem" }}>{user.uid}</strong></td></tr>
              <tr><td>Email</td><td><strong>{user.email || "—"}</strong></td></tr>
              <tr><td>Name</td><td><strong>{user.name || "—"}</strong></td></tr>
            </tbody>
          </table>
          <table className="table is-fullwidth is-narrow">
            <thead>
              <tr>
                <th>Tier</th>
                <th style={{ textAlign: "center" }}>Active</th>
                <th>Expires</th>
              </tr>
            </thead>
            <tbody>
              <EntitlementToggle user={user} tier="supporter" onUpdate={refreshUser} />
              <EntitlementToggle user={user} tier="pro" onUpdate={refreshUser} />
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

function SentryTestPanel() {
  return (
    <>
      <hr />
      <p className="subtitle">Sentry Test</p>
      <p className="is-size-7 has-text-grey" style={{ marginBottom: "0.5rem" }}>
        Throws a synchronous error so Sentry's global error handler catches it. The thrown error appears in the Sentry project within ~1 min. Admin-only; safe to leave in prod.
      </p>
      <button
        className="button is-small is-danger is-outlined"
        onClick={() => {
          throw new Error("This is your first error!");
        }}
      >
        Break the world
      </button>
    </>
  );
}

function FeatureFlagDebugPanel() {
  const { flags, overrides, toggle, removeOverride } = useFlags();
  const [addFlag, setAddFlag] = useState<FeatureFlagName | "">("");

  // Union of live flags and override-only flags, sorted alphabetically
  const allFlagNames = Array.from(new Set([
    ...Object.keys(flags) as FeatureFlagName[],
    ...Object.keys(overrides) as FeatureFlagName[],
  ])).sort();

  // Flags available to add (not already in the table)
  const availableToAdd = ALL_FEATURE_FLAGS.filter(f => !allFlagNames.includes(f));

  return (
    <>
      <hr />
      <p className="subtitle">Feature Flags</p>
      <table className="table is-fullwidth is-narrow">
        <thead>
          <tr>
            <th>Flag</th>
            <th style={{ textAlign: "center" }}>Live</th>
            <th style={{ textAlign: "center" }}>Override</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {allFlagNames.map((name) => {
            const hasLive = name in flags;
            const liveValue = hasLive ? flags[name] === true : undefined;
            const hasOverride = name in overrides;
            const overrideValue = hasOverride ? overrides[name] === true : liveValue ?? false;
            const overrideDiffers = hasOverride && overrides[name] !== liveValue;
            return (
              <tr key={name}>
                <td>{name}</td>
                <td style={{ textAlign: "center" }}>
                  {hasLive
                    ? <span className="icon has-text-grey-light">
                      <i className={liveValue ? "icon-check is-large" : "icon-check-empty is-large"} aria-hidden="true"></i>
                    </span>
                    : <span className="has-text-grey-light">—</span>
                  }
                </td>
                <td style={{ textAlign: "center" }}>
                  <span className={`icon is-clickable ${overrideDiffers ? "has-text-info" : ""}`} onClick={() => toggle(name, !overrideValue)}>
                    <i className={overrideValue ? "icon-check is-large" : "icon-check-empty is-large"} aria-hidden="true"></i>
                  </span>
                </td>
                <td style={{ textAlign: "center" }}>
                  {hasOverride && (
                    <span className="icon is-small is-clickable has-text-grey" onClick={() => removeOverride(name)} title="Reset override">
                      <i className="icon-cancel" aria-hidden="true"></i>
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {availableToAdd.length > 0 && (
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <div className="select is-small">
            <select value={addFlag} onChange={(e) => setAddFlag(e.target.value as FeatureFlagName | "")}>
              <option value="">Add flag...</option>
              {availableToAdd.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <button
            className="button is-small is-info is-outlined"
            disabled={!addFlag}
            onClick={() => { if (addFlag) { toggle(addFlag, true); setAddFlag(""); } }}
          >
            Add
          </button>
        </div>
      )}
    </>
  );
}

export function AdminStatsPanel() {
  const { t } = useTranslation();
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadStats = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await fetchAdminStats()
      setStats(result)
    } catch (e: any) {
      setError(e.message || t("admin.stats.loadFailed"))
    } finally {
      setLoading(false)
    }
  }

  const hasLoaded = stats !== null || error !== null

  return (
    <FeatureGate flag="adminPanel">
      <hr />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <p className="subtitle">{t("admin.stats.title")}</p>
      </div>
      {error && <p className="has-text-danger">{error}</p>}
      {stats && <StatsTable stats={stats} />}
      {!stats && !error && loading && <p className="has-text-grey">{t("admin.stats.loading")}</p>}
      <Button size="small" color="info" outlined loading={loading} onClick={loadStats}>
        {hasLoaded ? t("admin.stats.refresh") : t("admin.stats.load")}
      </Button>
      <UserEntitlementPanel />
      <DiscreteOptionsMigrationPanel />
      <FeatureFlagDebugPanel />
      <SentryTestPanel />
    </FeatureGate>
  )
}
